/*
Purpose: Move a session to a different tutor, date or time.
Project: TCMS

The backend rejects a move that clashes with the target tutor's diary, so the caller
surfaces that as a field-level message rather than a generic failure.
*/

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Form, Modal, Row } from 'react-bootstrap';
import Button from '@mui/material/Button';
import Select from 'react-select';

/** Difference between two `HH:mm` values as `HH:mm`, wrapping past midnight. */
function calculateDuration(startTime, endTime) {
  if (!startTime || !endTime) {
    return '';
  }
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);
  let minutes = endHour * 60 + endMinute - (startHour * 60 + startMinute);
  if (minutes < 0) {
    minutes += 24 * 60;
  }
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

const EMPTY_FORM = { tutorKey: '', sessionDate: '', startTime: '', endTime: '' };

export default function RescheduleSessionModal({
  show,
  session,
  tutorOptions,
  isSubmitting,
  onCancel,
  onSave,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  // Seed from the session being edited each time the dialog opens.
  useEffect(() => {
    if (show && session) {
      setForm({
        tutorKey: session.tutorKey || '',
        sessionDate: session.date || '',
        startTime: session.startTime ? session.startTime.slice(0, 5) : '',
        endTime: session.endTime ? session.endTime.slice(0, 5) : '',
      });
      setErrors({});
    }
  }, [show, session]);

  function updateField(name, value) {
    setForm((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => ({ ...previous, [name]: undefined }));
  }

  function handleSubmit(event) {
    event.preventDefault();

    const nextErrors = {};
    if (!form.tutorKey) {
      nextErrors.tutorKey = 'Tutor is required.';
    }
    if (!form.sessionDate) {
      nextErrors.sessionDate = 'Date is required.';
    }
    if (!form.startTime) {
      nextErrors.startTime = 'Start time is required.';
    }
    if (!form.endTime) {
      nextErrors.endTime = 'End time is required.';
    }
    if (form.startTime && form.endTime && form.startTime >= form.endTime) {
      nextErrors.endTime = 'End time must be after start time.';
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    onSave({
      tutorKey: form.tutorKey,
      sessionDate: form.sessionDate,
      startTime: `${form.startTime}:00`,
      endTime: `${form.endTime}:00`,
      duration: calculateDuration(form.startTime, form.endTime),
    });
  }

  const duration = calculateDuration(form.startTime, form.endTime);

  return (
    <Modal show={show} onHide={onCancel} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Edit Session</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row>
            <Col lg="12" className="mb-3">
              <h6 className="form-lable required-field">Tutor</h6>
              <Select
                menuPlacement="auto"
                options={tutorOptions}
                value={tutorOptions.find((option) => option.value === form.tutorKey) || null}
                onChange={(option) => updateField('tutorKey', option ? option.value : '')}
                placeholder="Select tutor"
              />
              {errors.tutorKey ? <span className="error">{errors.tutorKey}</span> : null}
            </Col>

            <Col lg="12" className="mb-3">
              <h6 className="form-lable required-field">Date</h6>
              <Form.Control
                type="date"
                value={form.sessionDate}
                onChange={(event) => updateField('sessionDate', event.target.value)}
                className={errors.sessionDate ? 'input-error' : ''}
              />
              {errors.sessionDate ? <span className="error">{errors.sessionDate}</span> : null}
            </Col>

            <Col lg="6" md="6" className="mb-3">
              <h6 className="form-lable required-field">Start time</h6>
              <Form.Control
                type="time"
                value={form.startTime}
                onChange={(event) => updateField('startTime', event.target.value)}
                className={errors.startTime ? 'input-error' : ''}
              />
              {errors.startTime ? <span className="error">{errors.startTime}</span> : null}
            </Col>

            <Col lg="6" md="6" className="mb-3">
              <h6 className="form-lable required-field">End time</h6>
              <Form.Control
                type="time"
                value={form.endTime}
                onChange={(event) => updateField('endTime', event.target.value)}
                className={errors.endTime ? 'input-error' : ''}
              />
              {errors.endTime ? <span className="error">{errors.endTime}</span> : null}
            </Col>

            <Col lg="12">
              <h6 className="form-lable">Duration</h6>
              <Form.Control type="text" value={duration} readOnly />
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer className="pt-0">
          <div className="mx-auto d-flex gap-2">
            <Button variant="contained" className="primary-gray" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              className="primary-filled"
              disabled={isSubmitting}
            >
              Save
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

RescheduleSessionModal.propTypes = {
  show: PropTypes.bool.isRequired,
  session: PropTypes.shape({
    tutorKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    date: PropTypes.string,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
  }),
  tutorOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ).isRequired,
  isSubmitting: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

RescheduleSessionModal.defaultProps = {
  session: null,
  isSubmitting: false,
};
